import { jsx as _jsx } from "react/jsx-runtime";
/**
 * E-commerce Store Context
 * Manages selected store and permissions across the e-commerce app
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { EcommerceService } from '@/services/ecommerce.service';
import { useAuth } from '@/hooks/useAuth';
const EcommerceStoreContext = createContext(undefined);
export function EcommerceStoreProvider({ children, }) {
    const { user } = useAuth();
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Refresh stores from database
    const refreshStores = async () => {
        if (!user)
            return;
        try {
            setError(null);
            const data = await EcommerceService.getStoresForUser(user.id);
            setStores(data);
            // Set first store as default if we have stores now
            if (data.length > 0 && !selectedStore) {
                setSelectedStore(data[0]);
                setUserRole('admin');
            }
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load stores';
            setError(message);
            console.error('Error loading stores:', err);
        }
    };
    // Load stores on mount
    useEffect(() => {
        if (!user) {
            setIsLoading(false);
            return;
        }
        async function loadStores() {
            try {
                setError(null);
                const data = await EcommerceService.getStoresForUser(user.id);
                setStores(data);
                // Set first store as default
                if (data.length > 0 && !selectedStore) {
                    setSelectedStore(data[0]);
                    // Get user role for this store
                    // For now, assume admin role - can be enhanced later
                    setUserRole('admin');
                }
            }
            catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to load stores';
                setError(message);
                console.error('Error loading stores:', err);
            }
            finally {
                setIsLoading(false);
            }
        }
        loadStores();
    }, [user, selectedStore]);
    // Update user role when store changes
    useEffect(() => {
        if (selectedStore && user) {
            // For now, assume admin role - can be enhanced with actual role fetching
            setUserRole('admin');
        }
    }, [selectedStore, user]);
    const canEdit = userRole ? ['admin', 'manager'].includes(userRole) : false;
    const canAdmin = userRole === 'admin' || userRole === 'owner';
    const hasNoStores = stores.length === 0 && !isLoading;
    const value = {
        stores,
        selectedStore,
        setSelectedStore,
        userRole,
        isLoading,
        error,
        canEdit,
        canAdmin,
        hasNoStores,
        refreshStores,
    };
    return (_jsx(EcommerceStoreContext.Provider, { value: value, children: children }));
}
export function useEcommerceStore() {
    const context = useContext(EcommerceStoreContext);
    if (!context) {
        throw new Error('useEcommerceStore must be used within EcommerceStoreProvider');
    }
    return context;
}
